import React, { useState } from "react";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";
import { GiCrossedSwords, GiShield } from "react-icons/gi";
import {
  FaPercent,
  FaGamepad,
  FaArrowLeft,
  FaEye,
  FaHandsHelping,
  FaBolt,
} from "react-icons/fa";
import { STAT_LABELS } from "../components/ChartImpact";

// 🔹 Définition des familles de stats (dégâts, vision, tanking, utilité)
const STAT_SECTIONS = [
  {
    id: "damage",
    label: "Dégâts",
    icon: GiCrossedSwords,
    description:
      "Tu te distingues surtout par les dégâts que tu apportes à ton équipe.",
    match: (key) => /damage|degat|dmg/i.test(key),
  },
  {
    id: "vision",
    label: "Vision",
    icon: FaEye,
    description:
      "Tu domines la vision et le contrôle de la carte pour ton équipe.",
    match: (key) => /vision|ward/i.test(key),
  },
  {
    id: "tanking",
    label: "Tanking",
    icon: GiShield,
    description:
      "Tu encaisse beaucoup pour ton équipe et prends les coups à leur place.",
    match: (key) => /tank|tanked|mitig/i.test(key),
  },
  {
    id: "utility",
    label: "Utilité / CC",
    icon: FaHandsHelping,
    description:
      "Tu apportes beaucoup de contrôle, de peel ou de soutien à ton équipe.",
    match: (key) => /cc|control|heal|shield|utility/i.test(key),
  },
  {
    id: "early",
    label: "Early impact",
    icon: FaBolt,
    description:
      "Tu crées surtout de l'avantage dans les premières minutes.",
    match: (key) => /^early/i.test(key) || /killsOnLanersEarlyJungleAsJungler|killsOnOtherLanesEarlyJungleAsLaner/i.test(key),
  },
];

// 🔹 Calcule la section dominante en fonction des valeurs de currentImpact
function computeMainSection(currentImpact) {
  if (!currentImpact) return null;

  const scores = STAT_SECTIONS.map((section) => {
    let sum = 0;
    for (const [key, rawVal] of Object.entries(currentImpact)) {
      const val = Number(rawVal);
      if (!Number.isFinite(val)) continue;
      if (section.match(key)) {
        sum += val;
      }
    }
    return { section, score: sum };
  });

  let best = null;
  for (const entry of scores) {
    if (best === null || entry.score > best.score) {
      best = entry;
    }
  }

  if (!best || Math.abs(best.score) < 0.01) {
    return null;
  }

  return best; // { section, score }
}

const LightImpactPage = () => {
  const [selectedChampion, setSelectedChampion] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { summonerName, tag } = useParams();

  // data envoyée depuis HomePage
  const data = location.state?.data;

  const handleGoToFullImpact = () => {
    navigate(
      `/impact/${encodeURIComponent(summonerName || "")}/${encodeURIComponent(
        tag || ""
      )}`,
      { state: { data } }
    );
  };

  // Si l'utilisateur refresh la page : pas de state
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 text-white">
        <p className="mb-4 text-slate-300">
          Impossible de retrouver les données. Retourne à l&apos;accueil pour
          relancer une analyse.
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm"
        >
          Retour à l&apos;accueil
        </button>
      </div>
    );
  }

  // Données globales
  const games = data.games_analyzed ?? 0;
  const role = data.role ?? "inconnu";
  const winrate = data.winrate ?? null;
  const impact = data.impact || {};

  // Champions joués renvoyés par le backend
  const champions = data.champions || [];
  const topChampions = champions.slice(0, 5); // limite à 5

  // Champion actuellement sélectionné
  const selectedChampionData =
    selectedChampion != null
      ? topChampions.find((c) => c.champion === selectedChampion) || null
      : null;

  // Impact / games / winrate en fonction du filtre
  const currentImpact = selectedChampionData?.impact || impact;
  const currentGames =
    typeof selectedChampionData?.games === "number"
      ? selectedChampionData.games
      : games;
  const currentWinrate =
    typeof selectedChampionData?.winrate === "number"
      ? selectedChampionData.winrate
      : winrate;

  // Top 5 stats, triées du plus positif au plus négatif
  const topImpactStats = Object.entries(currentImpact || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const formatStatName = (stat) => {
    return STAT_LABELS[stat] || stat;
  };

  const getChampionImageUrl = (champ) =>
    `https://ddragon.leagueoflegends.com/cdn/14.21.1/img/champion/${champ}.png`;

  // 🔹 Profil dominant (dégâts / vision / tank / utilité)
  const mainSection = computeMainSection(currentImpact);

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      {/* Fond */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-slate-900 to-purple-900/40 pointer-events-none" />
      <div
        className="absolute top-1/2 left-1/2 w-[800px] h-[800px] 
                      bg-blue-600/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2 
                      pointer-events-none"
      />

      <div className="relative flex flex-col min-h-screen">
        {/* HEADER */}
        <header className="w-full border-b border-slate-800 bg-slate-950/80 backdrop-blur">
          <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
            <Link to="/" className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600/80 text-sm font-bold">
                IG
              </span>
              <span className="font-semibold text-lg tracking-tight">
                ImpactGame
              </span>
            </Link>

            <nav className="flex gap-4 text-sm text-slate-300">
              <Link to="/" className="hover:text-white transition">
                Accueil
              </Link>
              <Link to="/about" className="hover:text-white transition">
                À propos
              </Link>
            </nav>
          </div>
        </header>

        {/* CONTENU */}
        <main className="flex-1 flex justify-center px-4 py-10">
          <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
            {/* COLONNE GAUCHE : TOP 5 STATS */}
            {topImpactStats.length > 0 && (
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl shadow-xl px-4 py-4 text-sm">
                <h2 className="text-sm font-semibold text-slate-200 mb-3">
                  {selectedChampion
                    ? `Tes 5 stats les plus impactantes sur ${selectedChampion}`
                    : "Tes 5 stats les plus impactantes"}
                </h2>

                <div className="space-y-3">
                  {topImpactStats.map(([statName, value]) => {
                    const label = formatStatName(statName);
                    const numVal = Number(value) || 0;
                    const isPositive = numVal >= 0;

                    // ✅ magnitude 0 → 100
                    const magnitude = Math.min(Math.abs(numVal), 100);
                    const intensity = magnitude / 100; // 0 → 1

                    // ✅ couleur dynamique RGBA
                    const green = { r: 34, g: 197, b: 94 }; // green-500
                    const red = { r: 239, g: 68, b: 68 }; // red-500
                    const base = isPositive ? green : red;
                    const alpha = 0.25 + 0.75 * intensity; // 0.25 → 1

                    const barColor = `rgba(${base.r}, ${base.g}, ${base.b}, ${alpha})`;

                    return (
                      <div
                        key={statName}
                        className="bg-slate-950/70 border border-slate-800 rounded-xl px-3 py-2"
                      >
                        {/* LABEL + VALEUR */}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-300">
                            {label}
                          </span>
                          <span
                            className={`font-semibold ${
                              isPositive ? "text-green-400" : "text-red-400"
                            }`}
                          >
                            {numVal > 0 ? "+" : ""}
                            {numVal.toFixed(1)}%
                          </span>
                        </div>

                        {/* BARRE */}
                        <div className="mt-2 w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full"
                            style={{
                              width: `${magnitude}%`, // ✅ 100% = full bar
                              backgroundColor: barColor, // ✅ densité selon la valeur
                              transition: "width 0.4s ease",
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* COLONNE CENTRALE : CARTE JOUEUR */}
            <div className="lg:col-span-2">
              <div className="w-full bg-slate-900/80 border border-slate-800 rounded-2xl shadow-2xl px-8 py-8">
                {/* Bouton retour */}
                <button
                  onClick={() => navigate(-1)}
                  className="mb-4 inline-flex items-center gap-2 text-xs text-slate-400 hover:text-white"
                >
                  <FaArrowLeft /> Retour
                </button>

                {/* Carte joueur */}
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="h-16 w-16 rounded-full bg-blue-600/70 flex items-center justify-center text-2xl font-bold mb-3">
                    {summonerName?.[0]?.toUpperCase() || "?"}
                  </div>
                  <h1 className="text-2xl font-bold">
                    {summonerName}{" "}
                    <span className="text-slate-400">#{tag}</span>
                  </h1>
                  <p className="text-xs text-slate-400 mt-1">
                    Résumé de ton impact en SoloQ (queue 420)
                    {selectedChampion ? ` · Filtre : ${selectedChampion}` : ""}
                  </p>
                </div>

                {/* Stats clés (filtrées si champion sélectionné) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Rôle détecté */}
                  <div className="bg-slate-950/70 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3">
                    <GiShield className="text-2xl" />
                    <div className="text-left">
                      <div className="text-xs text-slate-400 uppercase">
                        Rôle détecté
                      </div>
                      <div className="text-lg font-semibold">
                        {role.toUpperCase()}
                      </div>
                    </div>
                  </div>

                  {/* Games analysées (filtrées) */}
                  <div className="bg-slate-950/70 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3">
                    <FaGamepad className="text-2xl" />
                    <div className="text-left">
                      <div className="text-xs text-slate-400 uppercase">
                        Games analysées
                      </div>
                      <div className="text-lg font-semibold">
                        {currentGames}
                      </div>
                    </div>
                  </div>

                  {/* Winrate (filtré) */}
                  <div className="bg-slate-950/70 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3">
                    <FaPercent className="text-2xl" />
                    <div className="text-left">
                      <div className="text-xs text-slate-400 uppercase">
                        Winrate (échantillon)
                      </div>
                      <div className="text-lg font-semibold">
                        {currentWinrate !== null
                          ? `${currentWinrate}%`
                          : "Bientôt"}
                      </div>
                    </div>
                  </div>

                  {/* Profil dominant */}
                  <div className="bg-slate-950/70 border border-slate-800 rounded-xl px-4 py-3 flex items-center gap-3">
                    {mainSection ? (
                      <>
                        <mainSection.section.icon className="text-2xl" />
                        <div className="text-left">
                          <div className="text-xs text-slate-400 uppercase">
                            Profil dominant
                          </div>
                          <div className="text-lg font-semibold">
                            {mainSection.section.label}
                          </div>
                          <div className="text-xs text-slate-500">
                            {selectedChampion
                              ? `Sur ${selectedChampion}, tu es surtout un profil ${mainSection.section.label.toLowerCase()}.`
                              : `Sur cet échantillon, tu es surtout un profil ${mainSection.section.label.toLowerCase()}.`}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <GiCrossedSwords className="text-2xl" />
                        <div className="text-left">
                          <div className="text-xs text-slate-400 uppercase">
                            Profil dominant
                          </div>
                          <div className="text-sm text-slate-500">
                            On manque encore de données pour déterminer ton
                            profil principal.
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* CTA stats détaillées */}
                <div className="mt-4 text-center">
                  <button
                    onClick={handleGoToFullImpact}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm"
                  >
                    Stats détaillées
                  </button>
                </div>
              </div>
            </div>

            {/* COLONNE DROITE EXT : CHAMPIONS JOUÉS */}
            {topChampions.length > 0 && (
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl shadow-2xl px-6 py-6">
                <h2 className="text-sm font-semibold text-slate-200 mb-3">
                  Champions joués (échantillon)
                </h2>

                <div className="space-y-3">
                  {topChampions.map((c) => {
                    const isActive = selectedChampion === c.champion;
                    return (
                      <button
                        key={c.champion}
                        type="button"
                        onClick={() =>
                          setSelectedChampion((prev) =>
                            prev === c.champion ? null : c.champion
                          )
                        }
                        className={`w-full flex items-center gap-3 text-left rounded-xl px-3 py-2 transition border
                          ${
                            isActive
                              ? "bg-blue-600/30 border-blue-400"
                              : "bg-slate-950/70 border-slate-800 hover:bg-slate-900"
                          }`}
                      >
                        {/* Image du champion */}
                        <div className="h-12 w-12 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0">
                          <img
                            src={getChampionImageUrl(c.champion)}
                            alt={c.champion}
                            className="h-full w-full object-cover"
                          />
                        </div>

                        {/* Infos */}
                        <div className="flex flex-col">
                          <span className="text-sm text-slate-200 font-medium">
                            {c.champion}
                          </span>
                          <span className="text-xs text-slate-400">
                            {c.games} games • {c.winrate}%
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* FOOTER */}
        <footer className="border-t border-slate-800 bg-slate-950/90">
          <div className="max-w-5xl mx-auto px-4 py-4 text-xs text-slate-400 text-center sm:text-left">
            © {new Date().getFullYear()} ImpactGame · Projet non affilié à Riot
            Games.
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LightImpactPage;
